import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CdkScrollable } from '@angular/cdk/scrolling';
import {
  COMPANY_OBJECTIVE_TITLE_REJECTION_REASON_LABELS,
  CompanyObjectiveTitleRejectionReason,
} from 'src/app/enums/company-objective-title-rejection-reason.enum';
import { OkrTitleValidationResult } from 'src/app/models/okr-management.model';
import { ApiService } from 'src/app/services/api.service';

export interface CompanyObjectiveFormDialogData {
  company_product_id: number;
  company_objective_period_id: number;
  company_id: number;
  objective_id?: number;
  title?: string;
  relatable_type: 'company' | 'company_product';
  mode: 'create' | 'edit';
  available_products?: { id: number; title: string }[];
  relatable_id?: number;
}

@Component({
  selector: 'app-company-objective-form-dialog',
  templateUrl: './company-objective-form-dialog.component.html',
  styleUrl: './company-objective-form-dialog.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    CdkScrollable,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatIconModule,
  ],
})
export class CompanyObjectiveFormDialogComponent {
  private readonly dialog_ref = inject(MatDialogRef<CompanyObjectiveFormDialogComponent, boolean>);
  private readonly fb = inject(FormBuilder);
  private readonly api = inject(ApiService);
  private readonly snack = inject(MatSnackBar);
  readonly data = inject<CompanyObjectiveFormDialogData>(MAT_DIALOG_DATA);

  readonly saving = signal(false);
  readonly validation = signal<OkrTitleValidationResult | null>(null);
  readonly submit_label = computed(() => {
    if (this.saving()) {
      return 'Comprobando objetivo…';
    }

    return this.data.mode === 'edit' ? 'Comprobar objetivo y guardar' : 'Comprobar objetivo y crear';
  });
  readonly rejection_reason_label = computed(() => {
    const result = this.validation();
    if (result === null || result.is_valid || result.rejection_reason === null) {
      return null;
    }

    return COMPANY_OBJECTIVE_TITLE_REJECTION_REASON_LABELS[result.rejection_reason as CompanyObjectiveTitleRejectionReason] ?? null;
  });

  readonly form = this.fb.group({
    title: [this.data.title ?? '', Validators.required],
    relatable_id: [this.data.relatable_id ?? this.data.available_products?.[0]?.id ?? null],
  });

  applySuggestion(suggestion: string): void {
    this.form.controls.title.setValue(suggestion);
    this.validation.set(null);
  }

  save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const title = this.form.controls.title.value ?? '';
    this.validation.set(null);
    this.saving.set(true);

    const request$ =
      this.data.mode === 'edit' && this.data.objective_id != null
        ? this.api.updateCompanyObjective(this.data.objective_id, { title })
        : this.api.storeCompanyObjective({
            company_objective_period_id: this.data.company_objective_period_id,
            title,
            relatable_type: this.data.relatable_type,
            relatable_id:
              this.data.relatable_type === 'company'
                ? this.data.company_id
                : (this.form.controls.relatable_id.value ?? this.data.relatable_id ?? this.data.company_product_id),
          });

    request$.subscribe({
      next: () => {
        this.saving.set(false);
        this.snack.open(this.data.mode === 'edit' ? 'Se ha actualizado correctamente' : 'Se ha creado correctamente.', 'Ok', {
          duration: 3000,
        });
        this.dialog_ref.close(true);
      },
      error: (error: unknown) => {
        this.saving.set(false);
        if (isOkrTitleValidationError(error)) {
          this.validation.set(error.error);
          return;
        }

        const message =
          error instanceof HttpErrorResponse && error.error?.error
            ? String(error.error.error)
            : 'No se pudo guardar el objetivo. Inténtalo de nuevo.';
        this.snack.open(message, 'Ok', { duration: 3000 });
      },
    });
  }

  close(): void {
    this.dialog_ref.close();
  }
}

export function isOkrTitleValidationError(error: unknown): error is HttpErrorResponse & { error: OkrTitleValidationResult } {
  if (!(error instanceof HttpErrorResponse) || error.status !== 422 || error.error == null || typeof error.error !== 'object') {
    return false;
  }

  return 'is_valid' in error.error && error.error.is_valid === false;
}
